import { createApi } from "@reduxjs/toolkit/query/react";

import {
  APIEndpoints,
  TIME_ATTENDANCE_BY_DATE,
  TIME_ATTENDANCE_CLOCK_IN,
  TIME_ATTENDANCE_CLOCK_OUT,
  TIME_ATTENDANCE_DRAWER,
  TIME_ATTENDANCE_REPORT,
  TIME_ATTENDANCE_STATS,
  TIME_ATTENDANCE_SUMMARY,
} from "@/constants/apiEndpoints";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  TGetTimeAttendanceReportDataRequest,
  TGetTimeAttendanceStatsResponse,
  TGetTimeAttendanceStatsRequest,
  TDayTimeAttendanceEmpClockInResponse,
  TDayTimeAttendanceEmpClockInRequest,
  TDayTimeAttendanceEmpClockOutResponse,
  IDayTimeAttendanceEmpClockOutRequest,
  TGetTimeAttendanceSummaryResponse,
  IGetTimeAttendanceSummaryRequest,
  ITimeAttendace,
  IGetTimeAttendanceReportData,
  IAttendanceExceptionLocationCondition,
  ISingleAttendanceException,
  IAttendanceExceptionEligibilityRequest,
  IAttendanceExceptionEligibility,
  IAttendanceActivity,
  IAttendanceDayDetailResponse,
} from "@/types/(my-space)/timeAttendace.interface";
import { setTimeAttendanceSlice } from "@/store/slices/time-attendance.slice";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";

export interface IMsAttendanceExceptionStats {
  total: number;
  pending: number;
  approved: number;
  autoApproved: number;
  rejected: number;
  cancelled: number;
}

export const timeAttendanceApi = createApi({
  reducerPath: "timeAttendanceApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getTimeAttendanceReport",
    "getTimeAttendanceStats",
    "getTimeAttendanceSummary",
    "getTimeAttendace",
    "AttendanceExceptions",
    "getAttendanceExceptionStats",
  ],
  endpoints: (builder) => ({
    toggleAttendance: builder.mutation<
      TDayTimeAttendanceEmpClockInResponse,
      TDayTimeAttendanceEmpClockInRequest
    >({
      query: (body) => {
        return {
          url: "time-attendance/toggle-clock",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getTimeAttendace", "getTimeAttendanceSummary"],
    }),
    getTimeAttendanceReport: builder.query<
      IApiResponse<IGetTimeAttendanceReportData>,
      TGetTimeAttendanceReportDataRequest
    >({
      query: (params) => ({
        url: TIME_ATTENDANCE_REPORT,
        method: "GET",
        params,
      }),
      providesTags: ["getTimeAttendanceReport"],
    }),
    getTimeAttendanceStats: builder.query<
      TGetTimeAttendanceStatsResponse,
      TGetTimeAttendanceStatsRequest
    >({
      query: () => {
        return {
          url: TIME_ATTENDANCE_STATS,
          method: "GET",
        };
      },
      providesTags: ["getTimeAttendanceStats"],
    }),
    userCheckIn: builder.mutation<
      TDayTimeAttendanceEmpClockInResponse,
      TDayTimeAttendanceEmpClockInRequest
    >({
      query: (body) => {
        return {
          url: TIME_ATTENDANCE_CLOCK_IN,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getTimeAttendanceSummary", "getTimeAttendace"],
    }),
    userCheckOut: builder.mutation<
      TDayTimeAttendanceEmpClockOutResponse,
      IDayTimeAttendanceEmpClockOutRequest
    >({
      query: (body) => {
        return {
          url: TIME_ATTENDANCE_CLOCK_OUT,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["getTimeAttendanceSummary", "getTimeAttendace"],
    }),
    getTimeAttendanceSummary: builder.query<
      TGetTimeAttendanceSummaryResponse,
      IGetTimeAttendanceSummaryRequest
    >({
      query: (params) => {
        return {
          url: TIME_ATTENDANCE_SUMMARY,
          method: "GET",
          ...(params && Object.keys(params).length > 0 ? { params } : {}),
        };
      },
      providesTags: ["getTimeAttendanceSummary"],
      onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.data) {
            dispatch(
              setTimeAttendanceSlice({
                timeAttendanceSummaryData: data.data,
              }),
            );
          }
        } catch (error: any) {
          console.error(error.data.message || "profile fetch failed");
        }
      },
    }),
    getTimeAttendanceDayDetail: builder.query<
      IAttendanceDayDetailResponse,
      { date: string }
    >({
      query: (params) => ({
        url: TIME_ATTENDANCE_DRAWER,
        method: "GET",
        params,
      }),
      // Share the summary tag so regularize/clock mutations refetch the drawer.
      providesTags: ["getTimeAttendanceSummary"],
    }),
    // All configured locations for a team/project scope — state-independent
    // (works whether or not the user is clocked in). Feeds the regularization
    // gap-cover location picker.
    getScopeLocations: builder.query<
      IApiResponse<{ locations: { id: number; label: string | null }[] }>,
      { scopeType: "TEAM" | "PROJECT"; targetId: number }
    >({
      query: (params) => ({
        url: "/time-attendance/scope-locations",
        method: "GET",
        params,
      }),
    }),
    getTimeAttendace: builder.query<
      IApiResponse<ITimeAttendace>,
      IApiGeneralParams
    >({
      query: (params) => {
        return {
          url: TIME_ATTENDANCE_BY_DATE,
          params,
        };
      },
      providesTags: ["getTimeAttendace"],
      // onQueryStarted: async (_body, { dispatch, queryFulfilled }) => {
      //   try {
      //     const { data } = await queryFulfilled;
      //     if (data.data) {
      //       dispatch(
      //         setTimeAttendanceSlice({
      //           checkedHistoryByUserId: data.data,
      //           lastCheckedTime: data.data[data.data.length - 1],
      //           clockIn: null,
      //           clockOut: null,
      //           userCheckinHistory: data.data,
      //         })
      //       );
      //     }
      //   } catch (error: any) {
      //     console.error(error?.data?.message ?? "Unable to fetch profile");
      //   }
      // },
    }),
    getAttendanceExceptions: builder.query<
      IApiResponse<ISingleAttendanceException[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions
          .GetAllAttendanceExceptions,
        params,
      }),
      providesTags: ["AttendanceExceptions"],
    }),
    getAttendanceExceptionStats: builder.query<
      IApiResponse<IMsAttendanceExceptionStats>,
      void
    >({
      query: () => ({
        url: `${APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions.GetAllAttendanceExceptions}/stats`,
        method: "GET",
      }),
      providesTags: ["getAttendanceExceptionStats"],
    }),
    getAttendanceExceptionById: builder.query<
      IApiResponse<ISingleAttendanceException>,
      number
    >({
      query: (id) => ({
        url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions.GetAttendanceExceptionById(
          id,
        ),
      }),
    }),
    createAttendanceException: builder.mutation<
      IApiResponse<ISingleAttendanceException>,
      FormData
    >({
      query: (body) => ({
        url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions
          .CreateAttendanceException,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "AttendanceExceptions",
        "getAttendanceExceptionStats",
        "getTimeAttendace",
        "getTimeAttendanceSummary",
      ],
    }),
    cancelAttendanceExceptionRequest: builder.mutation<
      IApiResponse<void>,
      number
    >({
      query: (id) => ({
        url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions.CancelAttendanceExceptionRequest(
          id,
        ),
        method: "PATCH",
      }),
      invalidatesTags: ["AttendanceExceptions", "getAttendanceExceptionStats"],
    }),
    getAttendanceExceptionLocationCondition: builder.query<
      IApiResponse<IAttendanceExceptionLocationCondition>,
      void
    >({
      query: () => ({
        url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions
          .GetAttendanceExceptionLocationCondition,
        method: "GET",
      }),
    }),
    checkAttendanceExceptionEligibility: builder.mutation<
      IApiPaginatedResponse<IAttendanceExceptionEligibility>,
      IAttendanceExceptionEligibilityRequest
    >({
      query: (body) => {
        return {
          url: APIEndpoints.MySpace.TimeAttendance.AttendanceExceptions
            .checkAttendanceExceptionEligibility,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getTimeAttendace", "getTimeAttendanceSummary"],
    }),
    updateQuickActivityLog: builder.mutation<
      IApiResponse<IAttendanceActivity>,
      {
        id: number;
        body: {
          quickActivityStartTime?: string;
          quickActivityEndTime?: string;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: APIEndpoints.MySpace.TimeAttendance.UpdateQuickActivityLog(id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["getTimeAttendanceSummary", "getTimeAttendace"],
    }),
    deleteMyTimeAttendanceById: builder.mutation<
      IApiResponse<{ deleted: number }>,
      number
    >({
      query: (id) => ({
        url: APIEndpoints.MySpace.TimeAttendance.DeleteTimeAttendanceById(id),
        method: "DELETE",
      }),
      invalidatesTags: ["getTimeAttendanceSummary", "getTimeAttendace"],
    }),
  }),
});

export const {
  useToggleAttendanceMutation,
  useGetTimeAttendanceReportQuery,
  useGetTimeAttendanceStatsQuery,
  useGetTimeAttendanceSummaryQuery,
  useGetTimeAttendanceDayDetailQuery,
  useLazyGetScopeLocationsQuery,
  useGetTimeAttendaceQuery,
  useGetAttendanceExceptionByIdQuery,
  useGetAttendanceExceptionsQuery,
  useGetAttendanceExceptionStatsQuery,
  useCreateAttendanceExceptionMutation,
  useCancelAttendanceExceptionRequestMutation,
  useGetAttendanceExceptionLocationConditionQuery,
  useCheckAttendanceExceptionEligibilityMutation,
  useUpdateQuickActivityLogMutation,
  useDeleteMyTimeAttendanceByIdMutation,
} = timeAttendanceApi;
